import { useEffect } from 'react'
import { AxiosError } from 'axios'
import type { IntercomUserJwtDTO } from '@api/fastAPI.schemas'
import { getGetIntercomUserJwtQueryKey, useGetIntercomUserJwt } from '@api/intercom/intercom'
import { useAuth0 } from '@auth0/auth0-react'
import Intercom, { shutdown, update } from '@intercom/messenger-js-sdk'
import { useModelStore } from 'src/components/pages/Editor/stores'
import { intercomAppId, intercomCompany } from 'src/constants'

/** Same Cypress/Playwright detection used e.g. in `EditorSceneLayout` and `CameraControllerV2`. */
const isE2E = typeof window !== 'undefined' && ('Cypress' in window || '__STATIK_E2E__' in window)

/**
 * Boots the Intercom messenger for the signed-in user and keeps the currently
 * opened project on the Intercom contact, so support conversations and tickets
 * carry the project context.
 *
 * The identity is the Auth0 account: accounts used to be shared between several
 * people, which is why a user once picked themselves from a hardcoded list, but
 * everyone has their own account now and an organisation says which company
 * they work for.
 *
 * The identity is proven to Intercom with a backend-signed JWT
 * (`/intercom/user-jwt`) so nobody can impersonate a colleague by booting the
 * messenger with their email. The identifying attributes (user_id/email and
 * the company) live inside the JWT - Intercom ignores unsigned versions of
 * them once a token is present - so the boot call only adds the display name
 * and layout. When the backend has no Intercom secret configured (local
 * development, `token: null`) or the request fails, the messenger falls back
 * to the old unsigned boot: it is a support channel and must never take the
 * app down with it, and Intercom itself rejects the unsigned boot once
 * enforcement is on.
 *
 * The project lands on the *contact* as custom attributes - the messenger
 * cannot write conversation or ticket attributes. Intercom throttles `update`
 * to 20 calls per 30 minutes, so it is only called when the project actually
 * changes.
 *
 * Disabled entirely under Cypress/Playwright - the widget's iframe/network
 * calls have nothing to do with the app under test and only add flakiness.
 */
const useIntercom = (): void => {
  const { isAuthenticated, user } = useAuth0()

  const project = useModelStore(state => state.project)

  // The email doubles as the Intercom user id. Without one there is nobody to
  // identify, so the messenger stays down rather than booting anonymously.
  const email = user?.email
  const name = user?.name

  // The token far outlives the fetch (1h), and a re-fetch would re-boot the
  // messenger for no reason, so it is fetched once per session. No onError
  // snackbar on purpose: failing to secure the support widget is nothing the
  // user can act on, and the unsigned fallback below keeps it working.
  const { data, isFetched } = useGetIntercomUserJwt<IntercomUserJwtDTO, AxiosError>({
    query: {
      // Keyed by the email so an infinitely stale token can never be served
      // for a different identity than it was signed for.
      queryKey: [...getGetIntercomUserJwtQueryKey(), email],
      enabled: !isE2E && isAuthenticated && !!email,
      staleTime: Infinity,
      retry: 1,
    },
  })
  const userJwt = data?.token ?? undefined

  useEffect(() => {
    if (isE2E || !isAuthenticated || !email || !isFetched) return

    Intercom({
      app_id: intercomAppId,
      alignment: 'left',
      name: name || email,
      ...(userJwt
        ? { intercom_user_jwt: userJwt }
        : { user_id: email, email, company: intercomCompany }),
    })

    return () => shutdown()
  }, [isAuthenticated, email, name, userJwt, isFetched])

  useEffect(() => {
    if (isE2E || !email || !project.guid || !project.name) return

    update({
      project_guid: project.guid,
      project_name: project.name,
    })
  }, [email, project])
}

export default useIntercom
